<template>
    <section class="cookie-category">
        <h2 class="joy-mb-4">{{ sectionTitle }}</h2>
        <p v-if="translatedDescription" class="joy-mb-8">{{ translatedDescription }}</p>
        <div v-if="cookiesForCategory.length" class="cookie-category__table-wrap">
            <table class="cookie-category__table" data-cookie-table :data-testid="`cookie-category-cookies-${categoryId}`">
                <thead>
                    <tr>
                        <th scope="col">{{ t('gdpr.cookies.policy.column.name') }}</th>
                        <th scope="col">{{ t('gdpr.cookies.policy.column.description') }}</th>
                        <th scope="col">{{ t('gdpr.cookies.policy.column.domain') }}</th>
                        <th scope="col">{{ t('gdpr.cookies.policy.column.duration') }}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(row, idx) in cookiesForCategory" :key="`${row.name}-${row.domain}-${idx}`">
                        <td class="cookie-category__break" :data-label="t('gdpr.cookies.policy.column.name')">{{ row.name }}</td>
                        <td class="cookie-category__purpose" :data-label="t('gdpr.cookies.policy.column.description')">
                            {{ cookieDescription(row.name) }}
                        </td>
                        <td :data-label="t('gdpr.cookies.policy.column.domain')">{{ row.domain }}</td>
                        <td class="cookie-category__nowrap" :data-label="t('gdpr.cookies.policy.column.duration')">
                            {{ row.duration }}
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </section>
</template>

<script setup lang="ts">
    import {computed} from 'vue';
    import {useTranslation} from '#imports';
    import cookiesData from '~/assets/data/cookies/cookies.json';

    type PolicyCookieRow = {
        name: string;
        domain: string;
        category: string;
        duration: string;
    };

    type CookiesData = {
        meta: {
            fetchedAt: string;
            categories: Array<{id: string; label: string}>;
        };
        cookies: PolicyCookieRow[];
    };

    const props = withDefaults(
        defineProps<{
            /** Category id, e.g. `necessary`, `performances`, `advertising`, `youtube`, `unknown` */
            categoryId: string;
            /** Overrides title (e.g. custom label when not covered by i18n) */
            title?: string;
            /** Overrides description */
            description?: string;
        }>(),
        {
            title: undefined,
            description: undefined,
        },
    );

    const snapshot = cookiesData as CookiesData;

    const {t} = useTranslation();

    /** Looser typing until `nuxt prepare` refreshes `AllTranslationsRecord` from common-messages. */
    const tPolicy = t as (key: string, params?: Record<string, string>) => string;

    const I18N_CATEGORY_STEPS = ['necessary', 'performances', 'advertising', 'youtube', 'unknown'] as const;
    type I18nCategoryStep = (typeof I18N_CATEGORY_STEPS)[number];

    function isI18nCategoryStep(value: string): value is I18nCategoryStep {
        return (I18N_CATEGORY_STEPS as readonly string[]).includes(value);
    }

    /** Back-compat for previous category id `marketing` (e.g. cached data). */
    function normalizeCategoryId(raw: string): string {
        if (raw === 'marketing') {
            return 'advertising';
        }
        return raw;
    }

    const categoryId = computed(() => normalizeCategoryId(props.categoryId.trim()));

    const labelFromMeta = computed(() => snapshot.meta.categories.find((c) => c.id === categoryId.value)?.label);

    const sectionTitle = computed(() => {
        if (props.title) {
            return props.title;
        }
        const id = categoryId.value;
        if (isI18nCategoryStep(id)) {
            return tPolicy(`gdpr.cookies.policy.category.${id}.title`);
        }
        return labelFromMeta.value ?? id;
    });

    const translatedDescription = computed(() => {
        if (props.description !== undefined) {
            return props.description;
        }
        const id = categoryId.value;
        if (isI18nCategoryStep(id)) {
            return tPolicy(`gdpr.cookies.policy.category.${id}.description`);
        }
        return '';
    });

    function cookieDescription(name: string): string {
        const key = `gdpr.cookies.policy.cookie.${name}`;
        const translated = tPolicy(key);
        if (translated === key) {
            return tPolicy('gdpr.cookies.policy.noDescription');
        }
        return translated;
    }

    const cookiesForCategory = computed(() => {
        const id = categoryId.value;
        if (!id) {
            return [];
        }
        return snapshot.cookies.filter((c) => c.category === id);
    });
</script>

<style lang="scss" scoped>
    $cookie-table-stack-bp: 48rem; /* 768px — match Joy small/tablet */

    .cookie-category {
        margin-bottom: var(--joy-core-spacing-8);
        max-width: 100%;
        overflow-x: auto;

        @media (max-width: #{$cookie-table-stack-bp - 0.0625rem}) {
            overflow-x: visible;
        }

        &__table-wrap {
            overflow-x: auto;

            @media (max-width: #{$cookie-table-stack-bp - 0.0625rem}) {
                overflow-x: visible;
            }
        }

        &__table {
            width: 100%;
            min-width: 0;
            border-collapse: collapse;
            font-size: var(--joy-font-size-primary-200);

            th,
            td {
                text-align: left;
                padding: var(--joy-core-spacing-2) var(--joy-core-spacing-3) var(--joy-core-spacing-2) 0;
                vertical-align: top;
            }

            th {
                font-weight: var(--joy-font-weight-bold);
                border-bottom: 1px solid var(--joy-color-neutral-30);
            }

            @media (min-width: $cookie-table-stack-bp) {
                th:first-child,
                td:first-child {
                    width: 200px;
                    min-width: 200px;
                    max-width: 200px;
                }
            }

            @media (min-width: $cookie-table-stack-bp) {
                tbody tr:not(:last-child) td {
                    border-bottom: 1px solid var(--joy-color-neutral-20);
                }
            }

            &[data-cookie-table] {
                @media (max-width: #{$cookie-table-stack-bp - 0.0625rem}) {
                    thead {
                        /* Visually hidden; `data-label` on cells is announced per row. */
                        position: absolute;
                        width: 1px;
                        height: 1px;
                        padding: 0;
                        margin: -1px;
                        overflow: hidden;
                        clip: rect(0, 0, 0, 0);
                        white-space: nowrap;
                        border: 0;
                    }

                    tbody {
                        display: block;
                    }

                    tbody tr {
                        display: block;
                        border: 1px solid var(--joy-color-neutral-20);
                        border-radius: var(--joy-core-radius-2);
                        padding: var(--joy-core-spacing-3);
                        margin-bottom: var(--joy-core-spacing-4);
                    }

                    tbody tr:last-child {
                        margin-bottom: 0;
                    }

                    tbody td {
                        display: block;
                        padding: var(--joy-core-spacing-2) 0;
                    }

                    tbody td:not(:last-child) {
                        border-bottom: 1px solid var(--joy-color-neutral-20);
                    }

                    tbody td::before {
                        content: attr(data-label);
                        display: block;
                        font-weight: var(--joy-font-weight-bold);
                        font-size: var(--joy-font-size-primary-100);
                        color: var(--joy-color-text-default);
                        margin-bottom: var(--joy-core-spacing-1);
                    }
                }
            }
        }

        &__nowrap {
            white-space: nowrap;

            @media (max-width: #{$cookie-table-stack-bp - 0.0625rem}) {
                white-space: normal;
            }
        }

        &__break {
            word-break: break-all;
        }

        &__purpose {
            white-space: normal;
            overflow-wrap: break-word;

            @media (min-width: $cookie-table-stack-bp) {
                min-width: 12rem;
                max-width: 22rem;
            }
        }
    }
</style>
